import angular from 'angular';
import _ from 'lodash';
import roles from 'app/validators/roles';
import { getDisplayUserNameSelector } from 'selectors';
import editAccountTemplate from './edit-account.html';
import AccountDetailsModule from './components/account-details/account-details-module';
import AccountStatusModule from './components/account-status/account-status-module';
import AccountRetailersModule from './components/account-retailers/account-retailers-module';

const mapStateToThis = (state) => {
  const { session, dictionary } = state;
  const displayUserName = getDisplayUserNameSelector(state);
  const { locale, isAdmin } = session;
  const terms = dictionary.terms[locale];

  return {
    displayUserName,
    session,
    terms,
    isAdmin,
  };
};

// eslint-disable-next-line no-undef
@Inject('$ngRedux', '$state', 'gettextCatalog', '$timeout', '$stateParams', 'Notifications', 'Responsive')
class EditAccountMainController {
  constructor() {
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, null)(this);
  }

  $onInit() {
    this.activeTabIdx = this.$stateParams.tabId * 1 || 0;
    this.retailerId = _.get(this, '$state.params.retailerId');
    this.userModel = _.cloneDeep(this.model.user.editAccount);
    this.setPageTitle();
    this.checkUserEditHimself();
    this.isLoading = false;
  }

  $onDestroy() {
    this.unsubscribe();
  }

  setPageTitle() {
    this.$state.params.pageTitle = `${this.gettextCatalog.getString('User')} - ${this.userModel.contact.firstName} ${
      this.userModel.contact.lastName
    }`;
  }

  checkUserEditHimself() {
    this.userId = _.get(this, '$state.params.id') * 1;
    this.sessionUserId = _.get(this, 'session.id') * 1;
    if (this.userId === this.sessionUserId) {
      this.api.canEditAccountDetails = false;
    }
  }

  reloadView() {
    return this.$state.go('user-viewAccountDetails', { tabId: 0, skipDirtyForms: true }, { reload: true });
  }

  goToManageUsers() {
    return this.$state.go('user-manageUsers', { skipDirtyForms: true });
  }

  setOnDeselect(handler) {
    this.onDeselect = handler;
  }

  cancelStatus() {
    this.model.user.editAccount.status = this.userModel.status;
  }

  onActivate() {
    return this.api.activate.run(this.model.user.editAccount).then(() =>
      this.Notifications.showAlert(
        this.gettextCatalog.getString('Account activated'),
        /// {{ active }} is Lottery term using to determine User status.
        this.gettextCatalog.getString('Success! User Access changed to {{ active }}.', { active: this.terms.ACTIVE.singular })
      )
    );
  }

  onSuspend() {
    return this.api.suspend.run(this.model.user.editAccount).then(() =>
      this.Notifications.showAlert(
        this.gettextCatalog.getString('Account deactivated'),
        /// {{ inactive }} is Lottery term using to determine User status.
        this.gettextCatalog.getString('Success! User Access changed to {{ inactive }}.', { inactive: this.terms.INACTIVE.singular })
      )
    );
  }

  onUpdatePersonalData() {
    return this.api.updatePersonalData
      .run(this.model.user.editAccount)
      .then(() => {
        this.userModel = _.cloneDeep(this.model.user.editAccount);
        this.setPageTitle();
      })
      .then(() => this.Notifications.alertSuccess(this.gettextCatalog.getString('User account updated successfully')));
  }

  onAuthenticateUser(template) {
    if (!this.session.isAdmin) {
      return this.onActivate(template);
    }
    this.Notifications.showComplexDialog({
      template,
      resolve: {
        userName: () => this.model.user.editAccount.username,
      },
    }).result.then(() =>
      this.api.authenticateUser
        .run(this.model.user.editAccount)
        .then(
          () =>
            this.Notifications.showAlert(
              this.gettextCatalog.getString('Account authenticated'),
              /// {{ active }} is Lottery term using to determine User status.
              this.gettextCatalog.getString('Success! User Access changed to {{ active }}.', { active: this.terms.ACTIVE.singular })
            ).result
        )
        .then(() => this.reloadView())
        .then(() => Promise.resolve(this.model))
    );
  }

  viewAsThisUser() {
    this.isLoading = true;
    const credentials = { userId: this.userModel.id };
    const restUrl = this.isUserAdmin ? this.session.rest.oauth.loginAsAdmin.url : this.session.rest.oauth.loginAs.url;
    const registrationForm = true;
    const loginAs = true;
    const acceptTerms = true;
    const tokens = undefined;
    const impersonatingDisplayName = this.displayUserName;
    return this.api.loginByRest
      .run(credentials, restUrl, acceptTerms, registrationForm, loginAs, tokens, impersonatingDisplayName)
      .then((newModel) => {
        this.confirm(newModel);
        this.isLoading = false;
      })
      .catch((err) => {
        console.error(err);
        this.close();
        this.isLoading = false;
      });
  }

  onLoginAs(template) {
    this.Notifications.showComplexDialog({
      template,
      resolve: {
        api: () => this.api,
        model: () => this.model,
        session: () => this.session,
        userModel: () => this.userModel,
        isUserAdmin: () => this.isUserAdmin(),
        displayUserName: () => this.displayUserName,
        viewAsThisUser: () => this.viewAsThisUser,
      },
    })
      .result.then((newModel) => this.api.redirectAfterLogin(newModel))
      .then(() => {
        this.Notifications.showAlert(
          this.gettextCatalog.getString('Success!'),
          this.gettextCatalog.getString('You have been logged in as user: {{name}}.', { name: this.userModel.contact.emailAddress })
        );
      });
  }

  onResendActivationEmail() {
    const authenticateText = this.gettextCatalog.getString(
      'If the user has trouble activating their account, you may do it for them through the ‘More Actions’ button on their page.'
    );
    this.api.resendActivationEmail.run(this.userModel).then(() =>
      this.Notifications.showAlert(
        this.gettextCatalog.getString('Account activation email has been sent to {{name}}', {
          name: this.userModel.contact.emailAddress,
        }),
        this.api.canAuthenticateUser ? authenticateText : ''
      )
    );
  }

  onUpdateUserPrivileges(user) {
    this.api.updateUserPrivileges
      .run(user)
      .then(() => this.Notifications.alertSuccess(this.gettextCatalog.getString('User privileges updated successfully')));
  }

  deleteUser() {
    this.api.deleteUser
      .run(this.model)
      .then((newModel) => {
        this.confirm(newModel);
      })
      .catch(() => this.confirm());
  }

  onDeleteUser(template) {
    this.Notifications.showComplexDialog({
      template,
      resolve: {
        userName: () => this.model.user.editAccount.username,
        deleteUser: () => this.deleteUser,
        api: () => this.api,
        $state: () => this.$state,
        goToManageUsers: () => this.goToManageUsers,
      },
    }).result.then(() => this.goToManageUsers());
  }

  isUserSecondary() {
    return roles.SECONDARY_USER_ROLES.includes(_.get(this.model, 'user.editAccount.userClass'));
  }

  isUserAdmin() {
    return roles.ADMIN_ROLES.includes(_.get(this.model, 'user.editAccount.userClass'));
  }
}

class EditAccountMainComponent {
  constructor() {
    this.template = editAccountTemplate;
    this.controller = EditAccountMainController;
    this.bindings = {
      model: '<',
      api: '<',
    };
    return this;
  }
}

export default angular
  .module('edit-account', [AccountStatusModule.name, AccountDetailsModule.name, AccountRetailersModule.name])
  .component('editAccountMain', new EditAccountMainComponent());



// WEBPACK FOOTER //
// ./src/modules/user/components/edit-account/edit-account-component.js